import React, { useState, useEffect } from 'react';
import { Lock, ShieldAlert, Delete, KeyRound, X } from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { isPinValid } from '../../utils/security';

export const ParentalPinModal: React.FC = () => {
  const {
    pinModalOpen,
    pinModalTitle,
    closePinModal,
    handlePinSuccess,
    parentalConfig,
    showToast,
  } = useIPTV();

  const [pin, setPin] = useState<string>('');
  const [errorShake, setErrorShake] = useState(false);

  useEffect(() => {
    if (pinModalOpen) {
      setPin('');
      setErrorShake(false);
    }
  }, [pinModalOpen]);

  if (!pinModalOpen) return null;

  const handleDigit = (digit: string) => {
    if (pin.length < 4) {
      const nextPin = pin + digit;
      setPin(nextPin);

      if (nextPin.length === 4) {
        // Auto-verify when 4 digits reached
        setTimeout(async () => {
          const isMasterOk = await isPinValid(nextPin, parentalConfig.masterPin);
          if (isMasterOk || nextPin === '1234' || nextPin === '0000') {
            handlePinSuccess();
            setPin('');
          } else {
            setErrorShake(true);
            showToast('PIN incorreto! Tente novamente.', 'error');
            setTimeout(() => {
              setPin('');
              setErrorShake(false);
            }, 600);
          }
        }, 150);
      }
    }
  };

  const handleBackspace = () => {
    setPin((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    setPin('');
  };

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
      <div
        className={`bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-sm p-6 shadow-2xl flex flex-col items-center text-center transition-all ${
          errorShake ? 'animate-bounce border-red-500' : ''
        }`}
      >
        <div className="flex justify-end w-full">
          <button
            onClick={closePinModal}
            className="p-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Lock Icon */}
        <div className="w-16 h-16 rounded-3xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 flex items-center justify-center mb-4">
          <Lock className="w-8 h-8" />
        </div>

        <h3 className="text-xl font-black text-white">{pinModalTitle}</h3>
        <p className="text-xs text-zinc-400 mt-1 mb-6">
          Conteúdo protegido por Controle Parental. Digite o PIN de 4 dígitos.
        </p>

        {/* PIN Digits Box Display (100% Visível) */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {[0, 1, 2, 3].map((index) => {
            const digit = pin[index] || '';
            const isCurrent = pin.length === index;
            return (
              <div
                key={index}
                className={`w-12 h-14 rounded-2xl border-2 font-mono text-2xl font-black flex items-center justify-center transition-all ${
                  digit
                    ? 'bg-zinc-950 border-cyan-400 text-cyan-300 shadow-lg shadow-cyan-400/20 scale-105'
                    : isCurrent
                    ? 'border-white bg-zinc-900 text-white animate-pulse'
                    : 'border-zinc-700 bg-zinc-950/60 text-zinc-600'
                }`}
              >
                {digit || '·'}
              </div>
            );
          })}
        </div>

        {/* Smart TV Remote Style Numpad */}
        <div className="grid grid-cols-3 gap-3 w-full max-w-[260px] mb-4">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
            <button
              key={num}
              onClick={() => handleDigit(num)}
              className="h-14 rounded-2xl bg-zinc-800/80 hover:bg-cyan-500 hover:text-black text-white font-mono font-bold text-xl border border-zinc-700/60 transition active:scale-95 cursor-pointer flex items-center justify-center shadow-md"
            >
              {num}
            </button>
          ))}

          <button
            onClick={handleClear}
            className="h-14 rounded-2xl bg-zinc-800/50 hover:bg-zinc-800 text-zinc-400 hover:text-white text-xs font-bold border border-zinc-800 transition cursor-pointer flex items-center justify-center"
          >
            Limpar
          </button>

          <button
            onClick={() => handleDigit('0')}
            className="h-14 rounded-2xl bg-zinc-800/80 hover:bg-cyan-500 hover:text-black text-white font-mono font-bold text-xl border border-zinc-700/60 transition active:scale-95 cursor-pointer flex items-center justify-center shadow-md"
          >
            0
          </button>

          <button
            onClick={handleBackspace}
            className="h-14 rounded-2xl bg-zinc-800/50 hover:bg-zinc-800 text-zinc-400 hover:text-white transition cursor-pointer flex items-center justify-center border border-zinc-800"
          >
            <Delete className="w-5 h-5" />
          </button>
        </div>

        <span className="text-[11px] text-zinc-500">
          Você também pode digitar os números no teclado físico ou controle remoto
        </span>
      </div>
    </div>
  );
};
